import { IImageSceneDict } from '@/components/Root';
import { IEntityActionTypes, ISceneActions, ITransCatDict, ITransCatTypes, IUserActions, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '@/zustand';
import * as Zappar from "@zappar/zappar-threejs";
import React, { useCallback, useEffect, useRef } from 'react'
import { Group } from 'three';
import ImageTrackerGroup from './ImageTrackerGroup';
import { ISceneComp } from '@r3f-component-data-structure';
import { useGetCurrentSceneId, useGetFirstSceneId } from '@/hooks/general';
import { useGetActiveTrackingImageData } from '@/components/hoc/hocCustomHooks';
import { useHasTrackingImageChanged } from './hooks';
import { getAllUsedImageTrackerIds } from '@/components/hoc/hocUtils';
import { getPlayingVideoIds, getVideoIdsBySceneId } from './utils';

interface IParentProps {
	onVisible: () => unknown;
	onNotVisible?: () => unknown;
	enabled: boolean;
	children: IImageSceneDict | undefined;
}

const ImageTrackerGroups: React.FunctionComponent<IParentProps> = ({enabled, onVisible, onNotVisible, children: sceneDict }) => {
	const sceneDispatch = useSceneStore(({ dispatch }) => dispatch);
	const userDispatch = useUserStore(({ dispatch }) => dispatch);
	const actionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const trackingImageDict = useDataStore((state) => state.trackingImageDict) ?? {};
	const groupRefs = useRef<{[index: string]: Group | null}>({})
	const imageTrackerRefs = useRef<{[index: string]: Group & {enabled: boolean}}>({})
	const counterRef = useRef(0);
	const frameCountRef = useRef(0);
	const isGrabAndGoEnabled = useUserStore(({ isGrabNGoEnabled }) => isGrabNGoEnabled);
	const transcatDict = useSceneStore((state) => state.transitionCategory);
	const linkSceneEnabledImageTrackerId = useUserStore((state) => state.linkSceneEnabledImageTrackerId)
	const isRoundRobinActive = useUserStore((state) => state.isRoundRobinActive);
	const firstSceneId = useGetFirstSceneId();
	const {id: activeTrackingImageId} = useGetActiveTrackingImageData() ?? {};
	const activeTrackingData = useGetActiveTrackingImageData();
	const trackingImageIdHasChanged = useHasTrackingImageChanged()
	const componentsById = useDataStore((state) => state.componentsById);
	const activeTrackingImageIdRef = useRef(activeTrackingImageId);
	const blockNotVisibleLogic = useRef(false)
	const hasInitialSetupRun = useRef(false)
	const currentSceneId = useGetCurrentSceneId();
	const currentSceneIdRef = useRef(currentSceneId);
	const prevActiveSceneIdRef = useRef<string | null>(null);
	const videoIdsPlayingBeforeTargetLossRef = useRef<string[]>([])


	useEffect(() => {
		currentSceneIdRef.current = currentSceneId;
	}, [currentSceneId])

	useEffect(() => {
		activeTrackingImageIdRef.current = activeTrackingImageId;
	}, [activeTrackingImageId])

	useEffect(() => {
		if (!isGrabAndGoEnabled || !trackingImageIdHasChanged || !activeTrackingImageIdRef.current) return;
		groupRefs.current[activeTrackingImageIdRef.current!]!.visible = true;
	}, [trackingImageIdHasChanged])

	const trackingImageIds = Object.keys(trackingImageDict ?? {});
	const firstSceneIsImageTracked = (componentsById?.[firstSceneId] as ISceneComp)?.trackingType === 'image';

	// This is required as the info if first scene is image tracked may not be immediately available
	// If the first scene is not image tracked don't automatically enable round robin
	useEffect(() => {
		if (!firstSceneIsImageTracked) return;
		userDispatch({
			type: IUserActions.SET_ROUND_ROBIN_ACTIVE,
			payload: true
		})
		if (trackingImageIds.length == 0 || hasInitialSetupRun.current) return;
		hasInitialSetupRun.current = true;
		userDispatch({
			type: IUserActions.SET_ENABLED_IMAGE_TRACKER_IDS,
			payload: trackingImageIds
		})
	}, [firstSceneIsImageTracked, trackingImageIds.length])

	// Disable all image trackers if global enable prop is set to false (i.e. current scene is not image tracked)
	useEffect(() => {
		if (enabled) return;
		const {current: imageTrackerDict} = imageTrackerRefs;
		for (const key in imageTrackerDict) {	
			const imageTracker = imageTrackerDict[key];
			if (!imageTracker) return;
			imageTracker.enabled = false;
		}
		prevActiveSceneIdRef.current = null;
	}, [enabled])

	useEffect(() => {
		const activeTrackingId = activeTrackingData?.id
		if (!isGrabAndGoEnabled || !activeTrackingId || !groupRefs?.current[activeTrackingId]) return;
		groupRefs!.current[activeTrackingId]!.visible = true;
	}, [isGrabAndGoEnabled])

	// If user action links to a scene within an image tracker, enable it while disabling all others
	useEffect(() => {
		if (!linkSceneEnabledImageTrackerId) return;
		blockNotVisibleLogic.current = true;
		userDispatch({
			type: IUserActions.SET_ROUND_ROBIN_ACTIVE,
			payload: false
		})
		const {current: imageTrackerDict} = imageTrackerRefs;
		for (const key in imageTrackerDict) {	
			const imageTracker = imageTrackerDict[key];
			if (!imageTracker) return;
			imageTracker.enabled = linkSceneEnabledImageTrackerId === key;
			// console.log('force enable image tracker', linkSceneEnabledImageTrackerId);
		}
	}, [linkSceneEnabledImageTrackerId])

	// Round robin mechanism which cycles through all image trackers and enables 
	// each one on every 4th zappar camera frame
	useEffect(() => {
		if (trackingImageIds.length === 0 || !isRoundRobinActive || !enabled) return;
		const _enabledImageTrackerIds = useUserStore.getState().enabledImageTrackerIds;
		const advanceImageTrackerCycle = () => {
			// Enable one tracking group at a time
			const {current: imageTrackerDict} = imageTrackerRefs;
			for (const trackerId in imageTrackerDict) {	
				const imageTracker = imageTrackerDict[trackerId]
				if (!imageTracker) continue;
				imageTracker.enabled = trackingImageIds.indexOf(trackerId) === counterRef.current && _enabledImageTrackerIds.includes(trackerId);
			}	
			if (counterRef.current < trackingImageIds.length -1) counterRef.current++
			else counterRef.current = 0;
		}

		const onFrameUpdate = () => {
			if (frameCountRef.current < 3) {
				frameCountRef.current++;
				return
			};
			
			// Advance enabled group cycling every 4th frame
			advanceImageTrackerCycle();
			frameCountRef.current = 0;
		}

		Zappar.onFrameUpdate.bind(onFrameUpdate);

		return () => {
			Zappar.onFrameUpdate.unbind(onFrameUpdate);
		}
	}, [trackingImageIds.length, isRoundRobinActive, enabled])

	// When tracking image recognised, stop round robin. If a new tracking image is recognised,
	// enter that scene and set category to hidden for all others. Then set scene to visible.
	const _onVisible = useCallback((trackerId: string) => {
		blockNotVisibleLogic.current = false;
		const {linkSceneEnabledImageTrackerId: _linkSceneEnabledImageTrackerId, imageTrackerStartScenes: startSceneDict} = useUserStore.getState();
		const {trackingImageDict: _trackingImageDict = {}} = useDataStore.getState();
		// const isGrabAndGoEnabled = useUserStore.getState().isGrabNGoEnabled;

		userDispatch({
			type: IUserActions.SET_TRACKING_IMAGE_VISIBLE,
			payload: { visible: true },
		});

		userDispatch({
			type: IUserActions.SET_ROUND_ROBIN_ACTIVE,
			payload: false
		})

		const newActiveSceneId = trackerId === _linkSceneEnabledImageTrackerId ? currentSceneIdRef.current : (startSceneDict[trackerId] ?? _trackingImageDict![trackerId].startSceneId);

		if (prevActiveSceneIdRef.current === newActiveSceneId) {
			actionDispatch({
				type: IEntityActionTypes.PLAY_VIDEOS_BY_ID,
				payload: {ids: videoIdsPlayingBeforeTargetLossRef.current}
			})
		} else {
			videoIdsPlayingBeforeTargetLossRef.current = [];

			prevActiveSceneIdRef.current = newActiveSceneId;
	
			// Link scene action already has set jute to the correct specific active scene id
			if (!_linkSceneEnabledImageTrackerId) {
				sceneDispatch?.({
					type: ISceneActions.SET_ACTIVE_SCENE_ID,
					activeSceneId: newActiveSceneId,
				});
			}
	
			const newDict: ITransCatDict = {};
			for (const key in transcatDict) {
				newDict[key] = ITransCatTypes.exit;
				if (key !== newActiveSceneId) {
					actionDispatch({
						type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
						payload: {ids: getVideoIdsBySceneId(key), currentTime: 0}
					})
				}
			}

			newDict[newActiveSceneId] = ITransCatTypes.enter;
			sceneDispatch?.({
				type: ISceneActions.SET_TRANSITION_CATEGORY,
				transCatDict: newDict,
			});
		}
		
		onVisible();
		if (groupRefs.current?.[trackerId]) groupRefs.current![trackerId]!.visible = true;
	}, [])

	// If a tracking image which is currently recognised (and thus visible) is lost, hide the group and start round robin
	const _onNotVisible = useCallback((trackerId: string) => {
		const {enabledImageTrackerIds: _linkSceneEnabledImageTrackerId, isGrabNGoActive} = useUserStore.getState();
		if (blockNotVisibleLogic.current || !groupRefs.current?.[trackerId]?.visible || !_linkSceneEnabledImageTrackerId.includes(trackerId)) return;
		//TODO: should I only hide if grab n go mode is false?
		if (!isGrabNGoActive) groupRefs.current![trackerId]!.visible = false;
		//TODO: should I change the transition category here?
		userDispatch({
			type: IUserActions.SET_ROUND_ROBIN_ACTIVE,
			payload: true
		})
		userDispatch({
			type: IUserActions.SET_LINK_SCENE_ENABLED_IMAGE_TRACKER_ID,
			payload: null
		})
		const allIsedTrackingImageIds = getAllUsedImageTrackerIds(useDataStore.getState().componentsById);

		userDispatch({
			type: IUserActions.SET_ENABLED_IMAGE_TRACKER_IDS,
			payload: allIsedTrackingImageIds
		})

		const isGrabAndGoEnabled = useUserStore.getState().isGrabNGoEnabled;
		
		videoIdsPlayingBeforeTargetLossRef.current = getPlayingVideoIds()
		if (!isGrabAndGoEnabled) {
			actionDispatch({
				type: IEntityActionTypes.PAUSE_VIDEOS_BY_ID,
				payload: {ids: videoIdsPlayingBeforeTargetLossRef.current}
			})
		} 

		onNotVisible?.();
	}, [])

    return (
		<>
			{trackingImageIds.map((id) => {
				return (
					<ImageTrackerGroup
						key={id}
						trackingImageId={id}
						onVisible={_onVisible}
						onNotVisible={_onNotVisible}
						enabled={enabled}
					>
						<group 
							name={`tracker-groups-${id}`} 
							ref={ref => {
								if (!groupRefs.current[id]) {
									groupRefs.current[id] = ref;
								} 
								if (!imageTrackerRefs.current[id]) {
									imageTrackerRefs.current[id] = (ref?.parent?.parent as any)?.imageTracker;
								}
							}} 
							visible={false}
						>
							{sceneDict?.[id]?.map(d => d.content)}
						</group>
					</ImageTrackerGroup>
				)
			})}
		</>
	)
}

export default ImageTrackerGroups;
